import { useMemo } from 'react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import Destinations from '../components/Destinations';
import { destinations } from '../data/destinations';
import { Link } from 'react-router-dom';
import { Globe, Plane, Briefcase, Map } from 'lucide-react';

const categories = [
  { id: 'city', label: 'Global Cities', icon: Globe, description: 'Core urban coordination hubs.' },
  { id: 'airport', label: 'Airports', icon: Plane, description: 'Critical transit gateways.' },
  { id: 'itinerary', label: 'AI Itineraries', icon: Map, description: 'Automated movement plans.' },
  { id: 'business-hub', label: 'Business Hubs', icon: Briefcase, description: 'Corporate orchestration centers.' }
];

export default function Explore() {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Explore Global Movement Nodes | Voyara AI",
    "description": "Discover the global network of cities, airports, and itineraries optimized by Voyara AI.",
    "url": "https://www.eclipsegroup.in/explore"
  }), []);

  return (
    <main className="relative z-10 pt-24">
      <SEO 
        title="Explore Global Movement Nodes — Voyara AI"
        description="Discover cities, airports, and business travel itineraries optimized by Voyara AI travel intelligence."
        canonical="https://www.eclipsegroup.in/explore"
        schema={schema}
      />
      
      <section className="global-container py-20">
        <Breadcrumbs trail={[{ name: 'Explore', path: '/explore' }]} />
        <div className="max-w-3xl mb-20">
          <h1 className="font-serif text-[clamp(2.5rem,5vw,4rem)] leading-[1.1] mb-8 tracking-tight">
            The Global <span className="italic text-white/70">Movement Network.</span>
          </h1>
          <p className="text-secondary text-lg leading-relaxed max-w-2xl">
            City and airport guides for business travel. Each one covers the airports, rail links and fare cards
            travellers actually use, answers the questions that come up on a trip there, and explains what Voyara AI
            is built to keep in step along the way.
          </p>
        </div>

        {/* Category Clusters (Topical Authority) */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-32">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="p-8 rounded-3xl liquid-glass border border-white/5 hover:border-white/10 transition-all group"
            >
              <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center mb-6 group-hover:bg-blue-500/10 transition-colors">
                <cat.icon className="w-6 h-6 text-white/40 group-hover:text-blue-400 transition-colors" />
              </div>
              <h2 className="text-primary font-medium mb-2">{cat.label}</h2>
              <p className="text-secondary text-sm leading-relaxed">{cat.description}</p>
            </div>
          ))}
        </div>

        <div className="mb-12">
          <h2 className="text-xs font-bold tracking-[0.4em] uppercase text-white/55 mb-12">Featured Active Nodes</h2>
          <Destinations />
        </div>

        {/* Programmatic Site Map (Internal Linking Graph) */}
        <div className="mt-32 pt-20 border-t border-white/[0.03]">
          <h3 className="text-xs font-bold tracking-[0.3em] uppercase text-white/55 mb-12 text-center">Global Node Directory</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-12 gap-y-8">
            {destinations.map(dest => (
              <Link 
                key={dest.slug} 
                to={`/destinations/${dest.slug}`}
                className="text-secondary hover:text-white transition-colors text-sm font-medium flex items-center gap-2 group"
              >
                <div className="w-1 h-1 rounded-full bg-white/10 group-hover:bg-blue-500/50 transition-colors" />
                {dest.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
